<div class="pr-4 pl-4 xl:pr-6 xl:pl-0 pt-4 pb-6 flex flex-col gap-3 " [formGroup]="form">
  <!-- beaut-545. The same card the booked visit shows, in scheduling mode:
       one customer block across both screens instead of two that drift apart.
       Nothing here is saved until the visit is, so the card emits its two
       options into this form rather than writing them to a visit that does not
       exist yet. -->
  <beautyapp-user-card
    [user]="visitAddFacade.user()"
    [scheduling]="true"
    [initialSendNotification]="form.get('sendNotification')?.value"
    [initialUserRequest]="form.get('onUserRequest')?.value"
    (selectUserEvent)="selectCustomer($event)"
    (updateUserEvent)="selectCustomer($event)"
    (addUser)="addNewUser()"
    (viewUser)="viewUser()"
    (verifyUser)="$event && verifyUser($event)"
    (updateSmsNotification)="form.get('sendNotification')?.setValue($event)"
    (updateUserRequest)="form.get('onUserRequest')?.setValue($event)"
  ></beautyapp-user-card>

  <!-- beaut-545. Who and when, in a card of its own so it lines up with the
       customer card above instead of floating on the page background.

       Three read-alike fields — label on top, 40px control below — so the row
       has one baseline, and the button sits on it.

       The employee is editable only while the visit is still empty. One booking
       carries one employee and this page creates one booking, so once a
       procedure is listed below the choice is settled: swapping then would
       disagree with rows already priced and time-slotted under the previous
       employee. -->
  <nz-card>
    <div class="booking-when">
      <div class="booking-when__fields">
        <div class="booking-when__field booking-employee" data-test="visit-employee">
          <span class="booking-when__label">{{ 'calendar.form.employee' | translate }}</span>
          @if (canChangeEmployee()) {
            <beautyapp-select
              class="booking-when__control"
              data-test="visit-employee-select"
              [ngModel]="selectedEmployeeId()"
              [ngModelOptions]="{ standalone: true }"
              (ngModelChange)="setEmployee($event)"
              [data]="employeeOptions()"
              valueMember="id"
              displayMember="fullName"
              placeholder="{{ 'calendar.form.employee' | translate }}"
              size="large"
            ></beautyapp-select>
          } @else {
            <div class="booking-when__readout">
              <span class="booking-when__value">{{ employee()?.user?.fullName }}</span>
              @if (employee()?.position?.name) {
                <span class="booking-when__muted">{{ employee()?.position?.name }}</span>
              }
              <span
                class="booking-when__lock"
                nz-tooltip
                [nzTooltipTitle]="'calendar.form.employeeLocked' | translate"
              >
                <i nz-icon nzType="lock" nzTheme="outline"></i>
              </span>
            </div>
          }
        </div>

        <div class="booking-when__field booking-when__field--date">
          <beautyapp-datepicker
            class="booking-when__control"
            label="{{ 'calendar.form.date' | translate }}"
            placeholder="{{ 'calendar.form.date' | translate }}"
            formControlName="bookingDate"
            required
            size="large"
            [disabled]="visitAddFacade.isCompleted"
          ></beautyapp-datepicker>
        </div>

        <div class="booking-when__field">
          <span class="booking-when__label">{{ 'calendar.form.startTime' | translate }}</span>
          <div class="booking-when__readout">
            <span class="booking-when__value">{{ bookingDate() | date: 'HH:mm' }}</span>
          </div>
        </div>

        <div class="booking-when__field booking-when__field--source">
          <beautyapp-select
            class="booking-when__control"
            formControlName="sourceId"
            label="{{ 'calendar.form.source' | translate }}"
            placeholder="{{ 'calendar.form.sourcePlaceholder' | translate }}"
            [data]="bookingSources()"
            valueMember="id"
            displayMember="name"
            size="large"
            [disabled]="visitAddFacade.isCompleted"
          ></beautyapp-select>
        </div>
      </div>

      <button nz-button nzType="primary" nzSize="large" (click)="addBooking()">
        <i nz-icon nzType="plus" nzTheme="outline"></i>
        პროცედურის დამატება
      </button>
    </div>

    <!-- Under the row, not beside the picker: inline it would grow one field
         taller than the others and break the baseline the row is built on. -->
    @if (canChangeEmployee() && employeeBusy()) {
      <div class="booking-when__busy" data-test="visit-employee-busy">
        <i nz-icon nzType="exclamation-circle" nzTheme="outline"></i>
        {{ 'calendar.form.employeeBusy' | translate }}
      </div>
    }
  </nz-card>
  @if (bookings().length > 0) {

    @for (booking of bookings(); track $index; let bookingIndex = $index) {
      <div class="border border-[#EAE7E7] px-4 py-3 rounded-md">
        <div class="flex flex-col justify-between gap-4">
          <div class="flex-1" nz-row nzJustify="space-between">
            <div nz-col class="flex gap-2">
              <div>
                თანამშრომელი: {{ booking.employee?.user?.fullName }}
              </div>
            </div>
            <div nz-col>
              <i
                nz-icon
                nzType="field-time"
                nzTheme="outline"
                style="color: #1890ff"
              ></i>
              {{ booking.startTimeDate | date: 'HH:mm' }} - {{ booking.endTimeDate | date: 'HH:mm' }}
            </div>
          </div>
          @if (booking.room){
            <div nz-col class="flex gap-2">
              <div>
                ოთახი: {{ booking.room?.name }}
              </div>
            </div>
          }
          @if (booking.bookingServices && booking.bookingServices.length) {
            @for (bookingService of booking.bookingServices; track $index; let last = $last; let
              serviceIndex = $index) {
              <div class="flex justify-between items-center gap-4"
                [class.border-b]="!last && booking.bookingServices.length > 1"
                [class.border-gray-100]="!last && booking.bookingServices.length > 1"
                [class.pb-3]="!last && booking.bookingServices.length > 1"
                [class.mb-3]="!last && booking.bookingServices.length > 1">
                <div class="flex flex-col gap-2">
                  <div class="flex items-center gap-2">
                    {{ bookingService?.service?.name }}
                    @if (bookingService?.offerPurchaseId) {
                      <nz-tag nzColor="gold" class="text-xs">
                        <i nz-icon nzType="gift" nzTheme="outline"></i>
                        {{ 'calendar.offerService' | translate }}
                      </nz-tag>
                    }
                    @if (bookingService?.packagePurchaseId) {
                      <nz-tag nzColor="cyan" class="text-xs">
                        <i nz-icon nzType="gift" nzTheme="outline"></i>
                        {{ 'calendar.packageService' | translate }}
                      </nz-tag>
                    }
                  </div>
                  <div class="flex items-center">
                    <ng-container *ngxPermissionsOnly="['field.employee_salary']">
                      <div>
                        @if (bookingService && bookingService.salaryPercent) {
                          <nz-tag nzColor="default">{{ bookingService.salaryPercent }}%</nz-tag>
                        }
                        @if (bookingService && bookingService.salaryFixed) {
                          <nz-tag nzColor="default">{{ bookingService.salaryFixed }}
                            <i class="lari lari-c"></i>
                          </nz-tag>
                        }
                      </div>
                    </ng-container>
                    <div>
                      <i
                        nz-icon
                        nzType="field-time"
                        nzTheme="outline"
                        style="color: #1890ff"
                      ></i>
                      {{ bookingService?.time }}
                    </div>
                    <nz-divider nzType="vertical"></nz-divider>
                    <div class="flex gap-1 items-center">
                      @if (+bookingService?.salePrice > 0) {
                        <div class="flex items-center gap-1">
                          <i
                            nz-icon
                            nzType="money-collect"
                            nzTheme="outline"
                            style="color: #1890ff"
                          ></i>
                          <div>
                            {{ bookingService.salePrice }} <i class="lari lari-c"></i>
                          </div>
                          <del>{{ bookingService.price }} <i class="lari lari-c"></i></del>
                        </div>
                      }
                      @if (+bookingService.salePrice <= 0) {
                        <div>
                          <i
                            nz-icon
                            nzType="money-collect"
                            nzTheme="outline"
                            style="color: #1890ff"
                          ></i>
                          {{ bookingService.price }} <i class="lari lari-c"></i>
                        </div>
                      }
                    </div>

                    @if (bookingService.serviceProducts && bookingService.serviceProducts.length) {
                      <nz-divider nzType="vertical"></nz-divider>
                      <i
                        nz-icon
                        nzType="shopping-cart"
                        nzTheme="outline"
                        style="color: #1890ff"
                      ></i>
                      {{ bookingService.serviceProducts.length }}
                    }

                  </div>
                  <div>
                    დამატებითი თანამშრომელი:

                    <div class="flex items-center gap-1">
                      @for (additionalEmployee of bookingService.additionalEmployees; track $index) {
                        <div class="flex items-center gap-1">
                          <div class="flex items-center gap-1">
                            {{ additionalEmployee?.employee?.user?.fullName }}
                          </div>
                          <!--
                            This block had NO permission guard at all: every
                            role saw the additional employees' salary, while
                            the main employee's salary two blocks up was
                            guarded. Found while building the per-branch
                            visibility feature; the omission predates it.
                          -->
                          <ng-container *ngxPermissionsOnly="['field.employee_salary']">
                            <div>
                              @if (additionalEmployee.salaryFixed) {
                                <nz-tag nzColor="default">{{ additionalEmployee.salaryFixed }}
                                  <i class="lari lari-c"></i>
                                </nz-tag>
                              } @else {
                                <nz-tag nzColor="default"> {{ additionalEmployee.salaryPercent }}% </nz-tag>
                              }
                            </div>
                          </ng-container>
                          @if ($index < bookingService.additionalEmployees.length - 1) {
                            <nz-divider nzType="vertical"></nz-divider>
                          }
                        </div>
                      }
                    </div>
                  </div>
                </div>
                <nz-space [nzSize]="16">
                  <button
                    *nzSpaceItem
                    (click)="editBookingService(bookingService, bookingIndex, serviceIndex)"
                    nz-button
                    nzType="default"
                    nzShape="circle"
                    [disabled]="visitAddFacade.isCompleted"
                    nz-tooltip="პროცედურის რედაქტირება"
                    >
                    <nz-icon nzType="edit" />
                  </button>
                  <button
                    *nzSpaceItem
                    nz-popconfirm
                    nzPopconfirmTitle="დარწმუნებული ხართ, რომ გსურთ პროცედურის წაშლა?"
                    (nzOnConfirm)="deleteBookingService(bookingService, bookingIndex, serviceIndex)"
                    nz-button
                    nzDanger
                    nzType="dashed"
                    nzShape="circle"
                    [disabled]="visitAddFacade.isCompleted"
                    nz-tooltip="პროცედურის წაშლა"
                    >
                    <nz-icon nzType="delete" />
                  </button>
                </nz-space>
              </div>

              @if (bookingService?.specServices && bookingService?.specServices?.length) {
                <div nz-row nzJustify="space-between" nzAlign="bottom" style="margin-top: 10px">
                  <div nz-col nzFlex="90%">
                    @for (specService of bookingService.specServices; track specService) {
                      <nz-tag
                        [nzColor]="'blue'"
                        style="margin-top: 2px"
                        >
                        {{ specService?.service?.name }}
                      </nz-tag>
                    }
                  </div>
                </div>
              }


              @if (!bookingService.salaryPercent && !bookingService.salaryFixed) {
                <div
                  class="text-xs font-semibold px-4 py-2 rounded-md bg-yellow-100 text-yellow-600 border border-yellow-500"
                  >
                  პროცედურაში არ არის მითითებული ხელფასის პროცენტი ან ფიქსირებული
                </div>
              }
            }

          } @else {
            <div
              class="flex justify-between items-center text-xs font-semibold px-4 py-2 rounded-md bg-yellow-100 text-yellow-600 border border-yellow-500">
              <div>
                პროცედურა არ არის მითითებული
              </div>

              <button nz-button nzType="primary" (click)="addBookingService(booking, bookingIndex)">
                დამატება
              </button>
            </div>

          }
          @if (booking.description) {
            <div
              class="text-xs font-semibold px-4 py-2 rounded-md bg-blue-50 text-primary-500 border border-blue-100"
              >
              {{booking.description}}
            </div>
          }


          <div class="flex justify-between items-center gap-4">
            <nz-space [nzSize]="16">

              <button
                *nzSpaceItem
                (click)="addBookingService(booking, bookingIndex)"
                nz-button
                nzType="default"
                nzShape="circle"
                [disabled]="visitAddFacade.isCompleted"
                nz-tooltip="პროცედურის დამატება"
                >
                <nz-icon nzType="plus" />
              </button>
              <button
                *nzSpaceItem
                (click)="editBooking(booking, bookingIndex)"
                nz-button
                nzType="default"
                nzShape="circle"
                [disabled]="visitAddFacade.isCompleted"
                nz-tooltip="ჯავშნის რედაქტირება"
                >
                <nz-icon nzType="edit" />
              </button>
              @if (visitAddFacade.status !== 2) {
                <button
                  *nzSpaceItem
                  nz-popconfirm
                  nzPopconfirmTitle="დარწმუნებული ხართ, რომ გსურთ ჯავშნის წაშლა?"
                  (nzOnConfirm)="deleteBooking(bookingIndex)"
                  nz-button
                  nzDanger
                  nzType="dashed"
                  nzShape="circle"
                  [disabled]="visitAddFacade.isCompleted"
                  nz-tooltip="ჯავშნის წაშლა"
                  >
                  <nz-icon nzType="delete" />
                </button>
              }
            </nz-space>
          </div>
        </div>
      </div>
    }
    <div class="flex justify-between items-center gap-3">
      <label
        nz-checkbox
        formControlName="sendNotification"
        style="margin-bottom: 10px"
        [nzDisabled]="visitAddFacade.isCompleted"
      >{{ 'addVisit.form.sendNotification' | translate }}</label>
    </div>


    <nz-divider/>

    <div>
      <div>
        ჯამში გადასახდელი: {{totalPrice()}}
      </div>
    </div>

    <div class="flex justify-between items-center gap-3">
      <div class="flex justify-between items-center gap-3">

      </div>

      <div class="flex justify-end items-center gap-3">
        <button
          nz-button
          nzType="default"
          nzSize="large"
          (click)="drawerRef.close()"
          >
          {{ 'addVisit.buttons.close' | translate }}
        </button>

        <button
          nz-button
          nzType="primary"
          nzSize="large"
          (click)="addVisit()"
          [nzLoading]="loading()"
          [disabled]="visitAddFacade.isCompleted || loading()"
          >
          {{ 'addVisit.buttons.saveVisit' | translate }}
        </button>


      </div>
    </div>
  } @else {
    <nz-empty
      [nzNotFoundContent]="contentTpl"
      >
      <ng-template #contentTpl>
        <span>
          ჯავშნის შესაქმნელად დაამატეთ პროცედურები
        </span>
      </ng-template>
    </nz-empty>
  }
</div>

